import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import api from '@/lib/api'
import { getUserTimezone } from '@/lib/timezone'
import { useToast } from '@/hooks/use-toast'
import { formatPhoneDisplay, cn } from '@/lib/utils'
import { format } from 'date-fns'
import { Calendar, PhoneIncoming, PhoneOutgoing, MessageSquare, Star, Check, MoreHorizontal, StickyNote, PhoneCall, Undo2, List, CalendarDays, Loader2, Trash2, AlertCircle, CheckCircle2, Phone, Copy, User, Plus, ExternalLink } from 'lucide-react'
import { PageHeader, StatCard, EmptyState } from '@/components/dashboard-ui'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import BookingsCalendar, { type CalendarView, type CalendarBooking, type CalendarContextTarget } from '@/components/bookings/BookingsCalendar'
import BookingDetailPanel from '@/components/bookings/BookingDetailPanel'
import CalendarContextMenu, { type CalendarContextMenuItem } from '@/components/bookings/CalendarContextMenu'
import RescheduleConfirmDialog, { type RescheduleConfirmation } from '@/components/bookings/RescheduleConfirmDialog'

type PageView = 'calendar' | 'list'

type PillTone = 'royal' | 'periwinkle' | 'amber' | 'green' | 'rose' | 'neutral'

interface ConnectedCalendarEvent {
  id: string
  summary: string
  start: string
  end: string
  allDay: boolean
  htmlLink?: string
  location?: string
  description?: string
  metadata?: Record<string, string>
}

function calendarEventToBooking(event: ConnectedCalendarEvent): CalendarBooking {
  const start = event.allDay ? `${event.start}T00:00:00` : event.start
  return {
    id: `calendar-${event.id}`,
    source: 'Connected Calendar',
    type: 'calendar',
    callerName: event.summary || 'Calendar event',
    phone: null,
    date: start,
    dateResolved: true,
    summary: event.location || event.description || null,
    sentiment: null,
    qualificationScore: null,
    caseType: null,
    duration: null,
    scheduledDate: event.start?.slice(0, 10) || null,
    scheduledTime: event.allDay ? null : format(new Date(event.start), 'h:mm a'),
    bookingType: event.allDay ? 'callback_followup' : 'appointment',
    addressed: true,
    isExternalCalendarEvent: true,
    externalHtmlLink: event.htmlLink,
    externalCalendarProvider: 'nylas',
    externalEventId: event.id,
    calendarEventLocation: event.location,
    calendarEventDescription: event.description,
    nylasEventId: event.id,
  }
}

const PILL_TONE: Record<PillTone, { bg: string; text: string; ring: string }> = {
  royal:      { bg: 'bg-brand-royal/10',       text: 'text-brand-royal',       ring: 'ring-brand-royal/30' },
  periwinkle: { bg: 'bg-brand-periwinkle/12',  text: 'text-brand-periwinkle',  ring: 'ring-brand-periwinkle/30' },
  amber:      { bg: 'bg-amber-500/12',         text: 'text-amber-700 dark:text-amber-400', ring: 'ring-amber-500/30' },
  green:      { bg: 'bg-emerald-500/12',       text: 'text-emerald-700 dark:text-emerald-400', ring: 'ring-emerald-500/30' },
  rose:       { bg: 'bg-rose-500/12',          text: 'text-rose-600 dark:text-rose-400',  ring: 'ring-rose-500/30' },
  neutral:    { bg: 'bg-muted',                text: 'text-muted-foreground', ring: 'ring-border' },
}

function StatPill({ label, value, valueText, tone, active, onClick }: {
  label: string
  value?: number
  valueText?: string
  tone: PillTone
  active?: boolean
  onClick?: () => void
}) {
  const t = PILL_TONE[tone]
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
        t.bg,
        active && `ring-1 ${t.ring}`,
        onClick && 'press-scale hover:brightness-110 cursor-pointer',
      )}
    >
      <span className={cn('font-bold tabular-nums', t.text)}>{valueText ?? value}</span>
      <span className="text-foreground/70">{label}</span>
    </Tag>
  )
}

// Builds the right-click menu items per target. Kept at module scope so the
// component closure stays light — the only thing this needs from the page is
// the bag of handlers passed via `ctx`.
function buildContextMenuItems(
  target: CalendarContextTarget,
  ctx: {
    handleEditNote: (b: CalendarBooking) => void
    handleToggleAddressed: (b: CalendarBooking) => void | Promise<void>
    handleDeleteBooking: (b: CalendarBooking) => void
    handleAddTask: (date: Date, hour?: number) => void
    navigate: (path: string) => void
    toast: (opts: { title: string; description?: string; variant?: 'destructive' }) => void
  }
): CalendarContextMenuItem[] {
  if (target.kind === 'slot') {
    return [{
      id: 'add-task-slot',
      label: 'Add task here',
      icon: Plus,
      onClick: () => ctx.handleAddTask(target.date, target.hour),
    }]
  }
  if (target.kind === 'day') {
    return [{
      id: 'add-task-day',
      label: `Add task on ${format(target.date, 'MMM d')}`,
      icon: Plus,
      onClick: () => ctx.handleAddTask(target.date),
    }]
  }
  // kind === 'booking'
  const b = target.booking
  if (b.isExternalCalendarEvent) {
    return b.externalHtmlLink ? [{
      id: 'open-calendar-event',
      label: 'Open calendar event',
      icon: ExternalLink,
      onClick: () => window.open(b.externalHtmlLink, '_blank', 'noopener,noreferrer'),
    }] : []
  }
  const items: CalendarContextMenuItem[] = []
  items.push({
    id: 'edit-note',
    label: b.note ? 'Edit note' : 'Add note',
    icon: StickyNote,
    onClick: () => ctx.handleEditNote(b),
  })
  items.push({
    id: 'toggle-handled',
    label: b.addressed ? 'Reopen' : 'Mark handled',
    icon: b.addressed ? Undo2 : Check,
    onClick: () => { void ctx.handleToggleAddressed(b) },
  })

  let needsSeparator = true
  if (b.phone) {
    items.push({
      id: 'call',
      label: `Call ${formatPhoneDisplay(b.phone)}`,
      icon: PhoneCall,
      separatorAbove: needsSeparator,
      onClick: () => { window.location.href = `tel:${b.phone}` },
    })
    needsSeparator = false
    items.push({
      id: 'copy-phone',
      label: 'Copy phone number',
      icon: Copy,
      onClick: async () => {
        try {
          await navigator.clipboard.writeText(b.phone || '')
          ctx.toast({ title: 'Phone number copied' })
        } catch {
          ctx.toast({ title: 'Copy failed', variant: 'destructive' })
        }
      },
    })
    items.push({
      id: 'view-lead',
      label: 'View lead',
      icon: User,
      onClick: () => ctx.navigate(`/leads?q=${encodeURIComponent(b.phone || '')}`),
    })
  }
  if (b.conversationId && b.type === 'chat') {
    items.push({
      id: 'view-chat',
      label: 'View chat history',
      icon: MessageSquare,
      separatorAbove: needsSeparator,
      onClick: () => ctx.navigate(`/conversations?id=${b.conversationId}`),
    })
    needsSeparator = false
  }

  items.push({
    id: 'delete',
    label: 'Delete',
    icon: Trash2,
    danger: true,
    separatorAbove: true,
    onClick: () => ctx.handleDeleteBooking(b),
  })
  return items
}

export default function BookingsPage() {
  const { toast } = useToast()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkBookingId = searchParams.get('bookingId')
  const [bookings, setBookings] = useState<CalendarBooking[]>([])
  const [loading, setLoading] = useState(true)
  const [timeframe, setTimeframe] = useState('all')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'attention' | 'handled'>('all')
  const [hasPhoneNumber, setHasPhoneNumber] = useState(false)
  const [pageView, setPageView] = useState<PageView>('calendar')
  const [calendarView, setCalendarView] = useState<CalendarView>(
    window.innerWidth < 768 ? 'day' : 'month'
  )

  // Detail panel state
  const [selectedBooking, setSelectedBooking] = useState<CalendarBooking | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)

  // Right-click context menu state. Coordinates are viewport-relative;
  // CalendarContextMenu clamps near edges so the menu never overflows.
  const [contextMenu, setContextMenu] = useState<{
    target: CalendarContextTarget
    x: number
    y: number
  } | null>(null)

  // Drag-to-reschedule confirm dialog state. Set when a drag drop fires
  // BookingsCalendar's onReschedule; cleared on cancel or successful save.
  const [reschedule, setReschedule] = useState<{
    booking: CalendarBooking
    proposedStart: Date
  } | null>(null)

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkDeleting, setBulkDeleting] = useState(false)

  // In-app confirm dialog (replaces browser window.confirm)
  const [confirmState, setConfirmState] = useState<{
    open: boolean
    title: string
    description: string
    confirmLabel: string
    onConfirm: () => void | Promise<void>
  }>({
    open: false,
    title: '',
    description: '',
    confirmLabel: 'Delete',
    onConfirm: () => {},
  })
  const closeConfirm = () => setConfirmState(s => ({ ...s, open: false }))

  useEffect(() => {
    loadBookings()
    api.get('/api/voice/numbers').then(res => {
      if (res.data.success && res.data.phoneNumbers?.length > 0) {
        setHasPhoneNumber(true)
      }
    }).catch(() => {})
  }, [timeframe])

  // When linked from a Lead timeline (?bookingId=…), open that booking's
  // detail panel automatically once the list loads. Clears the param after
  // so refreshing doesn't keep re-opening it.
  useEffect(() => {
    if (!deepLinkBookingId || bookings.length === 0) return
    const target = bookings.find(b => b.id === deepLinkBookingId)
    if (target) {
      setSelectedBooking(target)
      setDetailOpen(true)
      setSearchParams({}, { replace: true })
    }
  }, [deepLinkBookingId, bookings, setSearchParams])

  const loadBookings = async () => {
    setLoading(true)
    try {
      const tz = encodeURIComponent(getUserTimezone())
      const start = new Date()
      start.setFullYear(start.getFullYear() - 1)
      const end = new Date()
      end.setFullYear(end.getFullYear() + 1)
      const [bookingsRes, calendarRes] = await Promise.all([
        api.get(`/api/analytics/bookings?timeframe=${timeframe}&timezone=${tz}`),
        api.get('/api/integrations/calendar/events', {
          params: { start: start.toISOString(), end: end.toISOString() },
        }).catch(() => null),
      ])
      if (bookingsRes.data.success) {
        const localBookings: CalendarBooking[] = bookingsRes.data.bookings || []
        const localNylasIds = new Set(
          localBookings
            .map(b => b.nylasEventId)
            .filter((id): id is string => typeof id === 'string' && id.length > 0)
        )
        const calendarEvents: ConnectedCalendarEvent[] = calendarRes?.data?.events || []
        const externalBookings = calendarEvents
          .filter(event => !localNylasIds.has(event.id) && !event.metadata?.legalLadyBookingId)
          .map(calendarEventToBooking)
        setBookings([...localBookings, ...externalBookings])
      }
    } catch (error) {
      toast({ title: 'Error loading bookings', description: 'Please try refreshing the page.', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const sourceFilteredBookings = bookings.filter(b => {
    if (sourceFilter === 'all') return true
    if (sourceFilter === 'inbound') return b.source === 'Inbound Call'
    if (sourceFilter === 'outbound') return b.source === 'Outbound Call'
    if (sourceFilter === 'chat') return b.type === 'chat'
    if (sourceFilter === 'calendar') return b.type === 'calendar'
    return true
  })

  const filteredBookings = sourceFilteredBookings.filter(b => {
    if (statusFilter === 'attention') return !b.addressed
    if (statusFilter === 'handled') return !!b.addressed
    return true
  })
  const selectableBookings = filteredBookings.filter(b => !b.isExternalCalendarEvent)

  const [noteDialogBooking, setNoteDialogBooking] = useState<CalendarBooking | null>(null)
  const [noteText, setNoteText] = useState('')
  const [savingNote, setSavingNote] = useState(false)

  const handleSaveNote = async () => {
    if (!noteDialogBooking) return
    if (noteDialogBooking.isExternalCalendarEvent) return
    setSavingNote(true)
    const booking = noteDialogBooking
    setBookings(prev => prev.map(b => b.id === booking.id ? { ...b, note: noteText } : b))
    if (selectedBooking?.id === booking.id) {
      setSelectedBooking({ ...selectedBooking, note: noteText })
    }
    try {
      await api.patch(`/api/analytics/bookings/${booking.id}/addressed`, {
        note: noteText,
        type: booking.type
      })
      toast({ title: 'Note saved' })
      setNoteDialogBooking(null)
      setNoteText('')
    } catch {
      toast({ title: 'Error', description: 'Failed to save note.', variant: 'destructive' })
    } finally {
      setSavingNote(false)
    }
  }

  const handleToggleAddressed = async (booking: CalendarBooking) => {
    if (booking.isExternalCalendarEvent) return
    const newValue = !booking.addressed
    setBookings(prev => prev.map(b => b.id === booking.id ? { ...b, addressed: newValue } : b))
    // Also update the selected booking if the panel is open
    if (selectedBooking?.id === booking.id) {
      setSelectedBooking({ ...selectedBooking, addressed: newValue })
    }
    try {
      await api.patch(`/api/analytics/bookings/${booking.id}/addressed`, {
        addressed: newValue,
        type: booking.type
      })
    } catch (error) {
      setBookings(prev => prev.map(b => b.id === booking.id ? { ...b, addressed: !newValue } : b))
      if (selectedBooking?.id === booking.id) {
        setSelectedBooking({ ...selectedBooking, addressed: !newValue })
      }
      toast({ title: 'Error', description: 'Failed to update booking status.', variant: 'destructive' })
    }
  }

  const handleBookingClick = (booking: CalendarBooking) => {
    setSelectedBooking(booking)
    setDetailOpen(true)
  }

  const handleConfirmReschedule = async (result: RescheduleConfirmation) => {
    if (!reschedule) return
    const { booking } = reschedule
    if (booking.isExternalCalendarEvent) return
    try {
      const res = await api.post(`/api/analytics/bookings/${booking.id}/reschedule`, {
        type: booking.type,
        newStart: result.start.toISOString(),
        newEnd: result.end.toISOString(),
        sendClientEmail: result.sendClientEmail,
        updateConnectedCalendar: result.updateConnectedCalendar,
        resendAttorneyReminder: result.resendAttorneyReminder,
      })
      if (res.data?.success) {
        // Optimistic local update so the pill jumps without waiting on a refetch.
        setBookings(prev => prev.map(b => b.id === booking.id
          ? {
              ...b,
              date: result.start.toISOString(),
              dateResolved: true,
              scheduledDate: format(result.start, 'yyyy-MM-dd'),
              scheduledTime: format(result.start, 'h:mm a'),
              reminderSent: false,
              confirmationSent: false,
            }
          : b
        ))
        setReschedule(null)
        toast({
          title: 'Booking rescheduled',
          description: `Moved to ${format(result.start, 'MMM d, h:mm a')}`,
        })
        // Differentiated skip messaging: tell the user *why* the invite didn't
        // go out instead of always blaming org settings. The reason comes
        // back from the backend so we can be specific.
        if (result.sendClientEmail && res.data.inviteSent === false) {
          const reason = res.data.inviteSkipReason as
            | 'no_email' | 'org_disabled' | 'no_reply_to' | 'send_failed' | null
          const skipMessages: Record<string, { title: string; description: string }> = {
            no_email: {
              title: 'No client email on file',
              description: 'This booking has no captured email address — the prospect didn\'t share one during the call/chat. Open the booking and add an email to enable invites.',
            },
            org_disabled: {
              title: 'Outbound prospect emails are off',
              description: 'Enable "Email prospects calendar invites" in Settings → Notifications → Bookings to email prospects on changes.',
            },
            no_reply_to: {
              title: 'Reply-To not configured',
              description: 'Outbound emails are on but no Reply-To address is set. Add one in Settings → Notifications → Bookings → Client Emails.',
            },
            send_failed: {
              title: 'Email send failed',
              // Real provider error appended below so the user sees the
              // actual reason (e.g. "Invalid `to` email", "domain blocked",
              // "rate limit exceeded") instead of just "rejected, check it".
              description: 'The booking was rescheduled but the email did not go out.',
            },
          }
          const fallback = {
            title: 'Client email not sent',
            description: 'The reschedule saved but the prospect email did not go out.',
          }
          const msg = (reason && skipMessages[reason]) || fallback
          const sendError = res.data.inviteSendError as string | undefined
          const description = reason === 'send_failed' && sendError
            ? `${msg.description} Provider said: "${sendError}"`
            : msg.description
          toast({ title: msg.title, description, variant: 'destructive' })
        }
        // Authoritative refetch so any server-side normalisation lands.
        loadBookings()
      } else {
        throw new Error('Reschedule failed')
      }
    } catch (err: any) {
      // Walk the common error shapes so the toast surfaces the actual server
      // reason instead of the generic fallback. Keep the full error in the
      // console so DevTools shows status / response body when triaging.
      console.error('[reschedule] failed', err, err?.response?.data)
      const data = err?.response?.data
      const status = err?.response?.status
      const serverMsg =
        data?.message
        || data?.error
        || (typeof data === 'string' ? data : null)
        || err?.message
      const message = serverMsg
        ? (status ? `${serverMsg} (${status})` : serverMsg)
        : 'Could not reschedule. Please try again.'
      toast({ title: 'Reschedule failed', description: message, variant: 'destructive' })
      throw err
    }
  }

  const handleEditNote = (booking: CalendarBooking) => {
    if (booking.isExternalCalendarEvent) return
    setNoteDialogBooking(booking)
    setNoteText(booking.note || '')
  }

  const handleDeleteBooking = (booking: CalendarBooking) => {
    if (booking.isExternalCalendarEvent) {
      if (booking.externalHtmlLink) {
        window.open(booking.externalHtmlLink, '_blank', 'noopener,noreferrer')
      }
      return
    }
    const label = booking.type === 'task' ? 'task' : 'booking'
    setConfirmState({
      open: true,
      title: `Delete this ${label}?`,
      description: 'This cannot be undone.',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          if (booking.type === 'task') {
            await api.delete(`/api/analytics/bookings/tasks/${booking.id}`)
          } else {
            await api.delete(`/api/analytics/bookings/${booking.id}`, { data: { type: booking.type } })
          }
          setBookings(prev => prev.filter(b => b.id !== booking.id))
          setDetailOpen(false)
          setSelectedBooking(null)
          toast({ title: `${booking.type === 'task' ? 'Task' : 'Booking'} deleted` })
        } catch {
          toast({ title: 'Error', description: 'Failed to delete.', variant: 'destructive' })
        } finally {
          closeConfirm()
        }
      },
    })
  }

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return
    const count = selectedIds.size
    setConfirmState({
      open: true,
      title: `Delete ${count} booking${count > 1 ? 's' : ''}?`,
      description: 'This cannot be undone.',
      confirmLabel: `Delete ${count}`,
      onConfirm: async () => {
        setBulkDeleting(true)
        try {
          const idsArray = Array.from(selectedIds)
          const typesArray = idsArray.map(id => {
            const b = bookings.find(b => b.id === id)
            return b?.type || 'chat'
          })
          await api.post('/api/analytics/bookings/bulk-delete', { ids: idsArray, types: typesArray })
          setBookings(prev => prev.filter(b => !selectedIds.has(b.id)))
          setSelectedIds(new Set())
          toast({ title: `${idsArray.length} booking${idsArray.length > 1 ? 's' : ''} deleted` })
        } catch {
          toast({ title: 'Error', description: 'Failed to delete bookings.', variant: 'destructive' })
        } finally {
          setBulkDeleting(false)
          closeConfirm()
        }
      },
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === selectableBookings.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(selectableBookings.map(b => b.id)))
    }
  }

  const toggleSelect = (id: string) => {
    const target = bookings.find(b => b.id === id)
    if (target?.isExternalCalendarEvent) return
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleToggleReminder = async (
    booking: CalendarBooking,
    field: 'attorneyReminderEnabled' | 'clientConfirmationEnabled',
    value: boolean
  ) => {
    // Optimistic update
    const updated = { ...booking, [field]: value }
    setBookings(prev => prev.map(b => b.id === booking.id ? { ...b, [field]: value } : b))
    if (selectedBooking?.id === booking.id) setSelectedBooking(updated)
    try {
      await api.patch(`/api/analytics/bookings/${booking.id}/addressed`, {
        [field]: value,
        type: booking.type,
      })
    } catch {
      // Revert
      setBookings(prev => prev.map(b => b.id === booking.id ? { ...b, [field]: !value } : b))
      if (selectedBooking?.id === booking.id) setSelectedBooking({ ...booking, [field]: !value })
      toast({ title: 'Error', description: 'Failed to update reminder setting.', variant: 'destructive' })
    }
  }

  // Task creation state
  const [taskDialogOpen, setTaskDialogOpen] = useState(false)
  const [taskTitle, setTaskTitle] = useState('')
  const [taskDescription, setTaskDescription] = useState('')
  const [taskDate, setTaskDate] = useState('')
  const [taskTime, setTaskTime] = useState('')
  const [savingTask, setSavingTask] = useState(false)

  const handleAddTask = (date: Date, hour?: number) => {
    setTaskDate(format(date, 'yyyy-MM-dd'))
    setTaskTime(hour !== undefined ? `${hour.toString().padStart(2, '0')}:00` : '')
    setTaskTitle('')
    setTaskDescription('')
    setTaskDialogOpen(true)
  }

  const handleSaveTask = async () => {
    if (!taskTitle.trim()) return
    setSavingTask(true)
    try {
      // Format time for display
      let scheduledTime: string | null = null
      if (taskTime) {
        const [h, m] = taskTime.split(':').map(Number)
        const d = new Date()
        d.setHours(h, m, 0, 0)
        scheduledTime = format(d, 'h:mm a')
      }

      const res = await api.post('/api/analytics/bookings/tasks', {
        title: taskTitle.trim(),
        description: taskDescription.trim() || null,
        scheduledDate: taskDate || null,
        scheduledTime,
      })

      if (res.data.success) {
        setBookings(prev => [res.data.task, ...prev])
        toast({ title: 'Task added' })
        setTaskDialogOpen(false)
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to create task.', variant: 'destructive' })
    } finally {
      setSavingTask(false)
    }
  }

  const getSourceIcon = (booking: CalendarBooking) => {
    if (booking.type === 'calendar') return <Calendar className="h-3 w-3 text-violet-500" />
    if (booking.source === 'Inbound Call') return <PhoneIncoming className="h-3 w-3 text-brand-royal" />
    if (booking.source === 'Outbound Call') return <PhoneOutgoing className="h-3 w-3 text-brand-periwinkle" />
    return <MessageSquare className="h-3 w-3 text-brand-indigo" />
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="h-96 bg-muted animate-pulse rounded-lg" />
      </div>
    )
  }

  const isCalendarView = pageView === 'calendar'
  const totalBookings = sourceFilteredBookings.length
  const attentionCount = sourceFilteredBookings.filter(b => !b.addressed).length
  const handledCount = sourceFilteredBookings.filter(b => b.addressed).length
  const callCount = sourceFilteredBookings.filter(b => b.type === 'call').length
  const chatCount = sourceFilteredBookings.filter(b => b.type === 'chat').length

  // View-mode toggle + filters cluster — same in both views
  const viewControls = (
    <>
      <div className="flex items-center bg-muted rounded-md p-0.5">
        <Button
          variant="ghost"
          size="sm"
          className={`h-7 text-xs px-2.5 rounded-sm ${pageView === 'calendar' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
          onClick={() => setPageView('calendar')}
        >
          <CalendarDays className="h-3.5 w-3.5 sm:mr-1" />
          <span className="hidden sm:inline">Calendar</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={`h-7 text-xs px-2.5 rounded-sm ${pageView === 'list' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
          onClick={() => setPageView('list')}
        >
          <List className="h-3.5 w-3.5 sm:mr-1" />
          <span className="hidden sm:inline">List</span>
        </Button>
      </div>
      <Select value={sourceFilter} onValueChange={setSourceFilter}>
        <SelectTrigger className="w-[120px] sm:w-[150px] h-9 text-xs">
          <SelectValue placeholder="All Sources" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Sources</SelectItem>
          <SelectItem value="inbound">Inbound Calls</SelectItem>
          <SelectItem value="outbound">Outbound Calls</SelectItem>
          <SelectItem value="chat">Chat</SelectItem>
          <SelectItem value="calendar">Connected Calendar</SelectItem>
        </SelectContent>
      </Select>
      <Select value={timeframe} onValueChange={setTimeframe}>
        <SelectTrigger className="w-[110px] sm:w-[130px] h-9 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Time</SelectItem>
          <SelectItem value="7d">Last 7 days</SelectItem>
          <SelectItem value="30d">Last 30 days</SelectItem>
          <SelectItem value="90d">Last 90 days</SelectItem>
        </SelectContent>
      </Select>
    </>
  )

  return (
    <div className={cn(
      'flex flex-col min-w-0',
      // Desktop: pin the calendar to viewport height so its internal scroll
      // handles the hour grid. Mobile lets the page itself scroll naturally
      // — the calendar's toolbar is sticky so it stays visible, and the user
      // gets one familiar scroll instead of nested scroll containers.
      isCalendarView && 'md:h-[calc(100dvh-5rem)]',
      !isCalendarView && 'space-y-5',
    )}>
      {isCalendarView ? (
        /* Compact header for calendar view — pill stat bar instead of bento. */
        <div className="rounded-2xl border bg-card shadow-tinted p-4 sm:p-5 mb-4 shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3 sm:mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-brand-royal/20 to-brand-periwinkle/15 border border-brand-royal/20 flex items-center justify-center shrink-0">
                <Calendar className="h-4 w-4 text-brand-royal" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Schedule</p>
                <h1 className="text-lg sm:text-xl font-heading font-semibold tracking-tight leading-tight">Bookings Calendar</h1>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">{viewControls}</div>
          </div>

          {/* Pill stat bar — compact alternative to the bento */}
          <div className="flex flex-wrap items-center gap-2">
            <StatPill label="Total" value={totalBookings} tone="royal"      active={statusFilter === 'all'}       onClick={() => setStatusFilter('all')} />
            <StatPill label="Needs attention" value={attentionCount} tone="amber" active={statusFilter === 'attention'} onClick={() => { const next = statusFilter === 'attention' ? 'all' : 'attention'; setStatusFilter(next); if (next !== 'all') setPageView('list') }} />
            <StatPill label="Handled"  value={handledCount}  tone="green"      active={statusFilter === 'handled'}    onClick={() => { const next = statusFilter === 'handled' ? 'all' : 'handled'; setStatusFilter(next); if (next !== 'all') setPageView('list') }} />
            <StatPill label="Calls / Chat" valueText={`${callCount} / ${chatCount}`} tone="periwinkle" />
          </div>
        </div>
      ) : (
        <>
          <PageHeader
            eyebrow="Schedule"
            title="Bookings Calendar"
            subtitle="Consultations scheduled by your AI agents from calls and chats."
            icon={
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand-royal/20 to-brand-periwinkle/15 border border-brand-royal/20 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-brand-royal" />
              </div>
            }
            actions={viewControls}
          />

          {/* Bento stats — only on List view, where vertical space is forgiving */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 stagger-fade">
            <StatCard
              className="col-span-2 lg:col-span-2 lg:row-span-2"
              label="Total bookings"
              value={totalBookings}
              icon={Calendar}
              tone={statusFilter === 'all' ? 'royal' : 'neutral'}
              onClick={() => setStatusFilter('all')}
            />
            <StatCard
              label="Needs attention"
              value={attentionCount}
              icon={AlertCircle}
              tone="amber"
              onClick={() => { const next = statusFilter === 'attention' ? 'all' : 'attention'; setStatusFilter(next); if (next !== 'all') setPageView('list') }}
            />
            <StatCard
              label="Handled"
              value={handledCount}
              icon={CheckCircle2}
              tone="green"
              onClick={() => { const next = statusFilter === 'handled' ? 'all' : 'handled'; setStatusFilter(next); if (next !== 'all') setPageView('list') }}
            />
            <StatCard
              className="col-span-2 lg:col-span-2"
              label="Calls / Chat"
              value={callCount}
              format={() => `${callCount} / ${chatCount}`}
              icon={Phone}
              tone="periwinkle"
            />
          </div>
        </>
      )}

      {/* Main Content */}
      {filteredBookings.length === 0 ? (
        <EmptyState
          icon={Calendar}
          tone="periwinkle"
          title="No bookings yet"
          description={hasPhoneNumber
            ? 'Bookings appear automatically when your AI agents schedule consultations during calls or chats.'
            : 'Bookings appear automatically when your AI agents schedule consultations during calls or chats. Assign a phone number to get started.'}
        />
      ) : pageView === 'calendar' ? (
        <div className="flex-1 min-h-0 mt-4">
          <BookingsCalendar
            bookings={filteredBookings}
            view={calendarView}
            onViewChange={setCalendarView}
            onBookingClick={handleBookingClick}
            onAddTask={handleAddTask}
            onContextMenu={(target, x, y) => setContextMenu({ target, x, y })}
            onReschedule={(booking, proposedStart) => setReschedule({ booking, proposedStart })}
          />
        </div>
      ) : (
        /* List View */
        <div className="space-y-2">
          {/* Bulk actions bar */}
          <div className="flex items-center gap-3 px-1">
            <input
              type="checkbox"
              checked={selectedIds.size === selectableBookings.length && selectableBookings.length > 0}
              onChange={toggleSelectAll}
              disabled={selectableBookings.length === 0}
              className="h-4 w-4 rounded border-border text-brand-primary focus:ring-brand-primary cursor-pointer"
            />
            <span className="text-xs text-muted-foreground">
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
            </span>
            {selectedIds.size > 0 && (
              <Button
                size="sm"
                variant="destructive"
                className="h-7 text-xs"
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
              >
                {bulkDeleting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Trash2 className="h-3 w-3 mr-1" />}
                Delete {selectedIds.size}
              </Button>
            )}
          </div>
          {filteredBookings.map((booking) => (
            <Card
              key={booking.id}
              className={`transition-shadow hover:shadow-sm cursor-pointer ${booking.addressed ? 'opacity-75' : ''} ${selectedIds.has(booking.id) ? 'ring-2 ring-brand-primary/30' : ''}`}
              onClick={() => handleBookingClick(booking)}
            >
              <CardContent className="py-3 px-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(booking.id)}
                    disabled={booking.isExternalCalendarEvent}
                    onChange={(e) => { e.stopPropagation(); toggleSelect(booking.id) }}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4 rounded border-border text-brand-primary focus:ring-brand-primary cursor-pointer shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
                  />
                  <div className={`w-1 self-stretch rounded-full shrink-0 ${booking.addressed ? 'bg-brand-royal/40' : 'bg-amber-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">
                        {booking.callerName || (booking.phone ? formatPhoneDisplay(booking.phone) : 'Unknown')}
                      </span>
                      {booking.caseType && (
                        <Badge variant="secondary" className="text-[11px] px-1.5 py-0 bg-brand-ice text-brand-indigo dark:bg-brand-deep dark:text-brand-periwinkle">{booking.caseType}</Badge>
                      )}
                      {booking.qualificationScore && (
                        <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
                          <Star className="h-2.5 w-2.5" />
                          {booking.qualificationScore}/10
                        </span>
                      )}
                    </div>
                    {booking.summary && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{booking.summary}</p>
                    )}
                    {booking.note && (
                      <p className="text-[11px] text-brand-royal mt-0.5 flex items-center gap-1 line-clamp-1">
                        <StickyNote className="h-2.5 w-2.5 shrink-0" />
                        {booking.note}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                      <span>{format(new Date(booking.date), 'MMM d, h:mm a')}</span>
                      <span className="text-border">|</span>
                      <span className="flex items-center gap-0.5">
                        {getSourceIcon(booking)}
                        <span className="ml-0.5">{formatDuration(booking.duration)}</span>
                      </span>
                      {booking.phone && (
                        <>
                          <span className="text-border">|</span>
                          <span>{formatPhoneDisplay(booking.phone)}</span>
                        </>
                      )}
                      {booking.sentiment && (
                        <>
                          <span className="text-border">|</span>
                          <span className={
                            booking.sentiment?.toLowerCase() === 'positive' ? 'text-emerald-500' :
                            booking.sentiment?.toLowerCase() === 'negative' ? 'text-red-500' : ''
                          }>
                            {booking.sentiment}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                    {booking.isExternalCalendarEvent && booking.externalHtmlLink ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs px-2.5"
                        onClick={() => window.open(booking.externalHtmlLink, '_blank', 'noopener,noreferrer')}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        Open
                      </Button>
                    ) : !booking.addressed ? (
                      <Button
                        size="sm"
                        className="bg-brand-royal hover:bg-brand-indigo text-white h-7 text-xs px-2.5"
                        onClick={() => handleToggleAddressed(booking)}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Handled
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground h-7 text-xs px-2"
                        onClick={() => handleToggleAddressed(booking)}
                      >
                        <Undo2 className="h-3 w-3 mr-1" />
                        Reopen
                      </Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {booking.isExternalCalendarEvent && booking.externalHtmlLink && (
                          <DropdownMenuItem onClick={() => window.open(booking.externalHtmlLink, '_blank', 'noopener,noreferrer')}>
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Open calendar event
                          </DropdownMenuItem>
                        )}
                        {!booking.isExternalCalendarEvent && booking.phone && (
                          <DropdownMenuItem onClick={() => { window.location.href = `tel:${booking.phone}` }}>
                            <PhoneCall className="h-4 w-4 mr-2" />
                            Call {formatPhoneDisplay(booking.phone)}
                          </DropdownMenuItem>
                        )}
                        {!booking.isExternalCalendarEvent && (
                          <>
                            <DropdownMenuItem onClick={() => handleEditNote(booking)}>
                              <StickyNote className="h-4 w-4 mr-2" />
                              {booking.note ? 'Edit Note' : 'Add Note'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleToggleAddressed(booking)}>
                              {booking.addressed ? (
                                <><Undo2 className="h-4 w-4 mr-2" />Reopen</>
                              ) : (
                                <><Check className="h-4 w-4 mr-2" />Mark Handled</>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={() => handleDeleteBooking(booking)}>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Drag-to-reschedule confirm dialog. Opens when BookingsCalendar fires
          onReschedule after a successful drop. User can fine-tune time and
          toggle which cascade actions fire (email client / update connected calendar /
          resend attorney reminder) before saving. */}
      <RescheduleConfirmDialog
        open={!!reschedule}
        booking={reschedule?.booking ?? null}
        proposedStart={reschedule?.proposedStart ?? null}
        onCancel={() => setReschedule(null)}
        onConfirm={handleConfirmReschedule}
      />

      {/* Right-click context menu — month/week/day views fire onContextMenu
          with a target descriptor; this builds the action list per target. */}
      <CalendarContextMenu
        open={!!contextMenu}
        x={contextMenu?.x ?? 0}
        y={contextMenu?.y ?? 0}
        items={contextMenu ? buildContextMenuItems(contextMenu.target, {
          handleEditNote,
          handleToggleAddressed,
          handleDeleteBooking,
          handleAddTask,
          navigate,
          toast,
        }) : []}
        onClose={() => setContextMenu(null)}
      />

      {/* Booking Detail Panel (slide-out) */}
      <BookingDetailPanel
        booking={selectedBooking}
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setSelectedBooking(null) }}
        onToggleHandled={handleToggleAddressed}
        onEditNote={handleEditNote}
        onDelete={handleDeleteBooking}
        onToggleReminder={handleToggleReminder}
      />

      {/* Notes Dialog */}
      <Dialog open={!!noteDialogBooking} onOpenChange={(open) => !open && setNoteDialogBooking(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {noteDialogBooking?.note ? 'Edit Note' : 'Add Note'}
            </DialogTitle>
            <DialogDescription>
              {noteDialogBooking?.callerName || (noteDialogBooking?.phone ? formatPhoneDisplay(noteDialogBooking.phone) : 'Booking')} — {noteDialogBooking?.source}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="e.g., Left voicemail, scheduled for Thursday 2pm, sent retainer..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            className="min-h-[100px] resize-none"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialogBooking(null)}>Cancel</Button>
            <Button onClick={handleSaveNote} disabled={savingNote}>
              {savingNote ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
            <DialogDescription>
              Create a task or reminder on the calendar
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="task-title" className="text-xs">Title</Label>
              <Input
                id="task-title"
                placeholder="e.g., Call back John Smith, Follow up on retainer..."
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="task-desc" className="text-xs">Description (optional)</Label>
              <Textarea
                id="task-desc"
                placeholder="Any additional details..."
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                className="min-h-[80px] resize-none"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="task-date" className="text-xs">Date</Label>
                <Input
                  id="task-date"
                  type="date"
                  value={taskDate}
                  onChange={(e) => setTaskDate(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <Label htmlFor="task-time" className="text-xs">Time</Label>
                <Input
                  id="task-time"
                  type="time"
                  value={taskTime}
                  onChange={(e) => setTaskTime(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveTask} disabled={savingTask || !taskTitle.trim()}>
              {savingTask ? 'Adding...' : 'Add Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog — replaces browser window.confirm so destructive
          actions match the rest of the app's styling. */}
      <Dialog open={confirmState.open} onOpenChange={(open) => !open && closeConfirm()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{confirmState.title}</DialogTitle>
            <DialogDescription>{confirmState.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={closeConfirm} disabled={bulkDeleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmState.onConfirm()}
              disabled={bulkDeleting}
            >
              {bulkDeleting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…</>
              ) : (
                confirmState.confirmLabel
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
